import React, { useState, useEffect } from 'react';
import {
  faPuzzlePiece,
  faChartLine,
  faNetworkWired,
  faLink,
  faXmark,
  faCopy
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './styles.css';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../../../../../config/api';
import { useTranslation } from 'react-i18next';
import ConfigWeb from '../../../../Configweb';
import { useWebSocket } from '../../../../../../hooks/useWebSocket';

const HighLevelDocModal = ({ onClose, apikey }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  const docUrl = i18n.language === 'pt'
    ? 'https://docs.wappfy.com.br/pt/integrations/leadconnector'
    : 'https://docs.wappfy.com.br/en/integrations/leadconnector';

  const truncateApiKey = (key, max = 20) => {
    if (!key) return '';
    return key.length > max ? key.substring(0, max) + '...' : key;
  };

  const handleCopyApiKey = () => {
    if (apikey) {
      navigator.clipboard.writeText(apikey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="integrations-container" onClick={onClose}>
      <div className="integrations-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2>{i18n.language === 'pt' ? 'Documentação HighLevel' : 'HighLevel Documentation'}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="modal-content">
          <div className="existing-integration-content">
            <p className="existing-integration-message">
              {i18n.language === 'pt'
                ? 'Para configurar a integração com o HighLevel, siga a documentação oficial.'
                : 'To configure the HighLevel integration, follow the official documentation.'}
            </p>

            {apikey ? (
              <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <label style={{ marginBottom: 0, fontSize: '14px', fontWeight: 500 }}>API Key:</label>
                  <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                    {truncateApiKey(apikey, 20)}
                  </span>
                  <button
                    onClick={handleCopyApiKey}
                    type="button"
                    style={{
                      background: copied ? '#10b981' : '#6366f1',
                      border: 'none',
                      color: 'white',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    {copied
                      ? (i18n.language === 'pt' ? 'Copiado!' : 'Copied!')
                      : (i18n.language === 'pt' ? 'Copiar' : 'Copy')}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="existing-integration-actions">
              <button
                className="dify-confirm"
                onClick={() => window.open(docUrl, '_blank')}
                type="button"
              >
                {i18n.language === 'pt' ? 'Acessar Documentação' : 'Access Documentation'}
              </button>
              <button className="dify-cancel" onClick={onClose} type="button">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HighLevelDataModal = ({ onClose, data, instanceName }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(data?.pipeline_id || '');
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [msgSignature, setMsgSignature] = useState(!!data?.msg_signature);
  const [savingSignature, setSavingSignature] = useState(false);

  const fetchPipelines = async () => {
    if (!data?.access_token) {
      setError('Token de acesso não encontrado.');
      return;
    }

    setLoadingPipelines(true);
    try {
      const response = await fetch(`https://services.leadconnectorhq.com/opportunities/pipelines?locationId=${data.location_id}`, {
        headers: {
          'Authorization': `Bearer ${data.access_token}`,
          'Content-Type': 'application/json',
          'Version': '2021-07-28'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setPipelines(result.pipelines || []);
    } catch (err) {
      console.error('Erro ao buscar pipelines:', err);
      setError('Erro ao carregar pipelines do HighLevel.');
    }
    setLoadingPipelines(false);
  };

  const handleSavePipeline = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(`/integrations/highlevel/${instanceName}`, {
        method: 'PUT',
        body: JSON.stringify({ pipeline_id: selectedPipeline })
      });
      setSuccess('Pipeline salvo com sucesso!');
      setEditMode(false);
      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError('Erro inesperado ao salvar pipeline.');
    }
    setLoading(false);
  };

  const handleRemove = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(`/integrations/highlevel/${instanceName}`, {
        method: 'DELETE'
      });
      setSuccess('Integração HighLevel removida com sucesso!');
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 1200);
    } catch (err) {
      setError('Erro inesperado ao remover integração.');
    }
    setLoading(false);
  };

  const handleToggleSignature = async (checked) => {
    setSavingSignature(true);
    setError('');
    setSuccess('');
    try {
      await apiRequest(`/integrations/highlevel/${instanceName}`, {
        method: 'PUT',
        body: JSON.stringify({ msg_signature: checked })
      });
      setMsgSignature(checked);
      setSuccess(t('integrations.highlevel.signatureSaved'));
      setTimeout(() => setSuccess(''), 1500);
    } catch (e) {
      setError(t('integrations.highlevel.signatureError'));
      setMsgSignature((prev) => !prev);
    }
    setSavingSignature(false);
  };

  const getPipelineName = (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    return pipeline ? pipeline.name : pipelineId || 'N/A';
  };

  // Buscar pipelines ao abrir o modal
  React.useEffect(() => {
    if (data?.access_token) {
      fetchPipelines();
    }
  }, [data?.access_token]);

  // Fechar modo de edição quando fechar modal
  React.useEffect(() => {
    return () => {
      setEditMode(false);
    };
  }, []);

  return (
    <div className="integrations-container" onClick={onClose}>
      <div className="integrations-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2>Integração HighLevel</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="modal-content">
          <div className="existing-integration-content">
            <p className="existing-integration-message">
              Integração HighLevel configurada para esta instância
            </p>

            <div className="highlevel-data-display">
              <div className="data-grid">
                <div className="data-item">
                  <label>Location ID:</label>
                  <span>{data.location_id || 'N/A'}</span>
                </div>
                <div className="data-item">
                  <label>{t('integrations.highlevel.ghlUserId')}:</label>
                  <span>{data.ghl_user_id || 'N/A'}</span>
                </div>
                <div className="data-item">
                  <label>Pipeline Selecionado:</label>
                  <span>{getPipelineName(data.pipeline_id)}</span>
                </div>
                <div className="data-item">
                  <label>{t('integrations.highlevel.signatureLabel')}:</label>
                  <span>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={msgSignature}
                        onChange={(e) => handleToggleSignature(e.target.checked)}
                        disabled={savingSignature}
                      />
                      {savingSignature ? t('integrations.highlevel.signatureSaving') : (msgSignature ? t('common.yes') : t('common.no'))}
                    </label>
                  </span>
                </div>
              </div>
            </div>

            {editMode && (
              <div className="pipeline-edit-section">
                <h3>Configurar Pipeline</h3>
                <div className="pipeline-selector">
                  <label htmlFor="pipeline-select">Selecionar Pipeline:</label>
                  {loadingPipelines ? (
                    <div className="loading-pipelines">Carregando pipelines...</div>
                  ) : (
                    <select
                      id="pipeline-select"
                      value={selectedPipeline}
                      onChange={(e) => setSelectedPipeline(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Selecione um pipeline</option>
                      {pipelines.map((pipeline) => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="pipeline-actions">
                  <button
                    className="dify-cancel"
                    onClick={() => {
                      setEditMode(false);
                      setSelectedPipeline(data?.pipeline_id || '');
                      setError('');
                    }}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    className="dify-confirm"
                    onClick={handleSavePipeline}
                    disabled={loading || !selectedPipeline}
                  >
                    {loading ? 'Salvando...' : 'Salvar Pipeline'}
                  </button>
                </div>
              </div>
            )}

            {!editMode && (
              <div className="existing-integration-actions">
                <button
                  className="dify-cancel"
                  onClick={handleRemove}
                  type="button"
                  disabled={loading}
                >
                  {loading ? 'Removendo...' : 'Remover Integração'}
                </button>
                <button
                  className="dify-confirm pipeline-config-btn"
                  onClick={() => setEditMode(true)}
                  type="button"
                  disabled={loading}
                >
                  Configurar Pipeline
                </button>
              </div>
            )}
            {success && <span className="success-message">{success}</span>}
            {error && <span className="error-message">{error}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

const N8nDocModal = ({ onClose }) => {
  const { t, i18n } = useTranslation();

  const docUrl = i18n.language === 'pt'
    ? 'https://docs.wappfy.com.br/pt/integrations/n8n'
    : 'https://docs.wappfy.com.br/en/integrations/n8n';

  return (
    <div className="integrations-container" onClick={onClose}>
      <div className="integrations-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2>{i18n.language === 'pt' ? 'Documentação n8n' : 'n8n Documentation'}</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <div className="modal-content">
          <div className="existing-integration-content">
            <p className="existing-integration-message">
              {i18n.language === 'pt'
                ? 'Para configurar a integração com o n8n, siga a documentação oficial.'
                : 'To configure the n8n integration, follow the official documentation.'}
            </p>

            <div className="existing-integration-actions">
              <button
                className="dify-confirm"
                onClick={() => window.open(docUrl, '_blank')}
                type="button"
              >
                {i18n.language === 'pt' ? 'Acessar Documentação' : 'Access Documentation'}
              </button>
              <button className="dify-cancel" onClick={onClose} type="button">
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IntegrationsModal = ({ onClose, instanceName, onWebhookInstance, connection }) => {
  const { t, i18n } = useTranslation();
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showHighLevelModal, setShowHighLevelModal] = useState(false);
  const [showHighLevelDocModal, setShowHighLevelDocModal] = useState(false);
  const [showN8nDocModal, setShowN8nDocModal] = useState(false);
  const [highLevelData, setHighLevelData] = useState(null);
  const [loadingHighLevel, setLoadingHighLevel] = useState(false);

  // ⭐ Conectar ao WebSocket para detectar criação de integração
  const { isConnected } = useWebSocket((message) => {
    // Quando detectar criação de integração HighLevel
    if (message.type === 'HIGHLEVEL_INTEGRATION_CREATED') {
      const { instanceId, integration } = message.data;

      if (instanceId === instanceName) {
        setShowHighLevelDocModal(false);
        setHighLevelData(integration);
        setShowHighLevelModal(true);
      }
    }

    // Quando detectar atualização
    if (message.type === 'HIGHLEVEL_INTEGRATION_UPDATED') {
      const { instanceId, integration } = message.data;
      if (instanceId === instanceName && showHighLevelModal) {
        setHighLevelData(integration);
      }
    }

    // Quando detectar remoção
    if (message.type === 'HIGHLEVEL_INTEGRATION_DELETED') {
      const { instanceId } = message.data;
      if (instanceId === instanceName) {
        setShowHighLevelModal(false);
        setHighLevelData(null);
      }
    }
  });

  const integrations = [
    {
      id: 'webhooks',
      title: t('integrations.webhooks.title'),
      icon: faLink,
      color: 'red',
      description: t('integrations.webhooks.description'),
      disabled: false
    },
    {
      id: 'make',
      title: t('integrations.make.title'),
      icon: faPuzzlePiece,
      color: 'purple',
      description: t('integrations.make.description'),
      disabled: false
    },
    {
      id: 'highlevel',
      title: t('integrations.highlevel.title'),
      icon: faChartLine,
      color: 'green',
      description: t('integrations.highlevel.description'),
      disabled: false
    },
    {
      id: 'n8n-agents',
      title: t('integrations.n8n.title'),
      icon: faNetworkWired,
      color: 'custom-pink',
      description: t('integrations.n8n.description'),
      disabled: false
    }
  ];

  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleWebhookClick = () => {
    setShowWebhookModal(true);
  };

  const handleWebhookClose = () => {
    setShowWebhookModal(false);
  };

  const checkHighLevelIntegration = async () => {
    setLoadingHighLevel(true);
    try {
      const data = await apiRequest(`/integrations/highlevel/${instanceName}`);
      setHighLevelData(data);
      setShowHighLevelModal(true);
    } catch (error) {
      console.error('Erro ao verificar integração HighLevel:', error);
      const redirectUri = encodeURIComponent(`${window.location.origin}/leadconnector`);
      window.open(`https://marketplace.leadconnectorhq.com/oauth/chooselocation?response_type=code&redirect_uri=${redirectUri}&client_id=66918be52e5e272f4e962463-m1l81eg5&scope=conversations%2Fmessage.readonly+conversations%2Fmessage.write+contacts.readonly+contacts.write+conversations%2Freports.readonly+conversations.readonly+conversations.write+users.readonly+opportunities.readonly+opportunities.write+workflows.readonly`, '_blank');
      setShowHighLevelDocModal(true);
    }
    setLoadingHighLevel(false);
  };

  const handleHighLevelClose = () => {
    setShowHighLevelModal(false);
    setHighLevelData(null);
  };

  const handleHighLevelDocClose = () => {
    setShowHighLevelDocModal(false);
  };

  const handleN8nDocClose = () => {
    setShowN8nDocModal(false);
  };

  return (
    <>
      <div className="integrations-container" onClick={onClose}>
        <div className="integrations-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="header-content">
              <h2>{t('integrations.title', { instanceName })}</h2>
            </div>
            <button className="close-button" onClick={handleClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="modal-content">
            <div className="integration-grid">
              {integrations.map((integration) => (
                <div
                  key={integration.id}
                  className={`integration-card ${integration.color}`}
                >
                  <div className="card-header">
                    <div className={`card-title ${integration.color}`}>
                      <FontAwesomeIcon icon={integration.icon} />
                      <span>{integration.title}</span>
                    </div>
                  </div>
                  <p className="card-description">{integration.description}</p>
                  <button
                    className={`connect-button ${integration.color}`}
                    disabled={integration.disabled || (integration.id === 'highlevel' && loadingHighLevel)}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (integration.disabled || (integration.id === 'highlevel' && loadingHighLevel)) return;
                      if (integration.id === 'make') {
                        window.open('https://www.make.com/en/hq/app-invitation/645aece21cb0c4490be1f8bd00ecd6f5', '_blank');
                      } else if (integration.id === 'highlevel') {
                        checkHighLevelIntegration();
                      } else if (integration.id === 'webhooks') {
                        handleWebhookClick();
                      } else if (integration.id === 'n8n-agents') {
                        setShowN8nDocModal(true);
                      }
                    }}
                  >
                    {integration.disabled
                      ? integration.devMsg
                      : (integration.id === 'highlevel' && loadingHighLevel)
                        ? 'Verificando...'
                        : integration.title}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showWebhookModal && (
        <ConfigWeb
          isOpen={showWebhookModal}
          onClose={handleWebhookClose}
          connection={{
            instance: instanceName,
            server_id: typeof instanceName === 'object' && instanceName?.server_id
              ? instanceName.server_id
              : undefined,
            ...(typeof instanceName === 'string'
              ? {}
              : {})
          }}
        />
      )}

      {showHighLevelModal && highLevelData && (
        <HighLevelDataModal
          onClose={handleHighLevelClose}
          data={highLevelData}
          instanceName={instanceName}
        />
      )}

      {showHighLevelDocModal && (
        <HighLevelDocModal
          onClose={handleHighLevelDocClose}
          apikey={connection?.apiKey || connection?.rawData?.apikey}
        />
      )}

      {showN8nDocModal && (
        <N8nDocModal
          onClose={handleN8nDocClose}
        />
      )}
    </>
  );
};

export default IntegrationsModal;
